// @ts-strict-ignore
import * as React from 'react';
import * as AnalyticEvents from '../../utils/analytics';
import { useDispatch, useSelector } from 'react-redux';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Modal from 'antd/lib/modal';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import Text from 'antd/lib/typography/Text';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { RootState } from '../../reducers/reducers';
import {
    selectAllAppGroupNamesInOrg,
    selectAllAppsIdsInOrg,
    selectOrg,
    selectPublicApiKey,
    selectOrgGroups,
} from '../../selectors/selectors';
import { PublicApiUpdatable } from '../../actions/updatable';
import { OrgComponentProps, UserAppData } from '../../typescriptHelpers';
import { AddApiKeyRequest, clearApiKey, tryAddPublicApiKey } from '../../actions/publicApi';
import { runwayColors } from '../styles/globalStyles';
import { allReleasePropertiesFrom } from '../../helpers/trackingHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import Select from 'antd/lib/select';
import { displayTextForUserGroupSingular } from '../../helpers/displayHelpers';
import { AppNameWithSecondaryPlatform, platformLabelForAppName } from '../../helpers/mdmPlatformOverride';
import Checkbox from 'antd/lib/checkbox';
import { UserPermissions } from '../../types';

interface AddApiKeyModalProps extends OrgComponentProps {
    allKeyNames: string[];
    open: boolean;
    onClose: () => void;
    allAvailableApps: UserAppData[];
}

const AddApiKeyModal: React.FC<AddApiKeyModalProps> = (props: AddApiKeyModalProps) => {
    const { allKeyNames, orgId, open, onClose } = props;
    const [form] = Form.useForm();
    const [createButtonDisabled, setCreateButtonDisabled] = React.useState(true);
    const { Option } = Select;

    const dispatch = useDispatch();

    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const appsInOrg = useSelector((state: RootState) => selectAllAppsIdsInOrg(state, orgId));
    const appGroupsInOrg = useSelector((state: RootState) => selectAllAppGroupNamesInOrg(state, orgId));

    const [copySuccess, setCopySuccess] = React.useState(false);
    const [selectedGroups, setSelectedGroups] = React.useState<string[]>([]);
    const [selectedApps, setSelectedApps] = React.useState<string[]>([]);
    const isAdminChecked = Form.useWatch('isAdmin', form);

    const addApiKeyIsLoading = useIsLoading(PublicApiUpdatable.NewPublicApiKeyEndpoint);
    const addApiKeySuccess = useUpdated(PublicApiUpdatable.NewPublicApiKeyEndpoint);
    const addApiKeyError = useError(PublicApiUpdatable.NewPublicApiKeyEndpoint);
    const createdApiKey = useSelector((state: RootState) => selectPublicApiKey(state));
    const orgGroupsById = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));

    const onSubmit = (form) => {
        form.validateFields()
            .then((values) => {
                const action = PublicApiUpdatable.NewPublicApiKeyEndpoint;
                const permissions = values.isAdmin ? [UserPermissions.Admin] : [];
                const request: AddApiKeyRequest = {
                    orgId,
                    name: values.name.trim(),
                    groups: values.groups,
                    permissions: permissions,
                    appIds: values.apps,
                };

                dispatch(tryAddPublicApiKey(action, request));
            })
            .then(() => {
                const properties = allReleasePropertiesFrom(org, null, null, appsInOrg, appGroupsInOrg);
                AnalyticEvents.track(AnalyticEvents.Event.AddPublicApiKey, properties);
            })
            .catch((error) => {
                globalMessage.error(error.message);
            });
    };

    React.useEffect(() => {
        if (addApiKeySuccess) {
            globalMessage.success('Added new API key!');
            form.resetFields();
            setCreateButtonDisabled(true);
        }
    }, [addApiKeySuccess]);

    React.useEffect(() => {
        if (addApiKeyError) {
            globalMessage.error(addApiKeyError);
        }
    }, [addApiKeyError]);

    React.useEffect(() => {
        const interval = setInterval(() => {
            setCopySuccess(false);
        }, 2000);

        return () => clearInterval(interval);
    }, [copySuccess]);

    const copyToClipboard = async (key) => {
        try {
            await navigator.clipboard.writeText(key);
            setCopySuccess(true);
        } catch {
            setCopySuccess(false);
        }
    };

    const handleAdminCheckBoxChange = () => {
        if (!isAdminChecked) {
            setSelectedGroups(form.getFieldValue('groups'));
            setSelectedApps(form.getFieldValue('apps'));
            form.setFieldsValue({ groups: [] });
            form.setFieldsValue({ apps: [] });
        } else {
            form.setFieldsValue({ groups: selectedGroups });
            form.setFieldsValue({ apps: selectedApps });
        }
    };

    return (
        <Modal
            open={open}
            destroyOnClose
            title={
                <div style={{ paddingRight: 24 }}>
                    <Typography.Text>Create a new API key</Typography.Text>
                </div>
            }
            onCancel={onClose}
            okText="Create"
            okButtonProps={{
                loading: addApiKeyIsLoading,
                disabled: createButtonDisabled,
            }}
            onOk={() => form.submit()}
            afterClose={() => {
                form.resetFields();
                dispatch(clearApiKey());
            }}
            focusTriggerAfterClose={false}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Form
                    form={form}
                    onFieldsChange={() =>
                        setCreateButtonDisabled(
                            !form.isFieldsTouched(false) ||
                                form.getFieldsError().some((field) => field.errors.length > 0),
                        )
                    }
                    onFinish={() => onSubmit(form)}
                    layout="vertical"
                >
                    <Form.Item
                        name={'name'}
                        label="API key name"
                        rules={[
                            { required: true },
                            {
                                validator: (rule, value) => {
                                    if (allKeyNames.includes(value.trim() as string)) {
                                        return Promise.reject('API key name must be unique ');
                                    } else {
                                        return Promise.resolve();
                                    }
                                },
                            },
                        ]}
                    >
                        <Input placeholder="My key" />
                    </Form.Item>
                    <Form.Item
                        name={'groups'}
                        label="Group scopes"
                        help="The API key will be granted access to perform the user actions that are allowed for the selected user group(s). Leave blank to make this API key read-only."
                    >
                        <Select
                            disabled={isAdminChecked}
                            mode="multiple"
                            allowClear
                            style={{ width: '100%' }}
                            placeholder={isAdminChecked ? 'All groups' : 'Select user group(s)'}
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={Object.values(orgGroupsById)
                                .filter((userGroup) => !userGroup.disabled)
                                .map((userGroup) => ({
                                    label: displayTextForUserGroupSingular(userGroup),
                                    value: userGroup.id,
                                }))}
                        ></Select>
                    </Form.Item>
                    <Form.Item
                        name={'apps'}
                        label={<Text style={{ marginTop: 15 }}>App scopes</Text>}
                        help="The API key will be granted access to read from and perform actions on the selected app(s). Leave blank to grant access to all apps."
                    >
                        <Select
                            disabled={isAdminChecked}
                            mode="multiple"
                            allowClear
                            style={{ width: '100%' }}
                            placeholder={isAdminChecked ? 'All apps' : 'Select user app(s)'}
                        >
                            {props.allAvailableApps.map((appData) => {
                                return (
                                    <Option value={appData.appId} key={appData.appId}>
                                        <AppNameWithSecondaryPlatform
                                            appName={appData.appName}
                                            platformLabel={platformLabelForAppName(appData)}
                                        />
                                    </Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label={<Text style={{ marginTop: 15 }}>Admin scope</Text>}
                        name="isAdmin"
                        valuePropName="checked"
                        help="API keys with the admin scope will be granted access to perform all actions across all apps"
                    >
                        <Checkbox checked={isAdminChecked} onChange={handleAdminCheckBoxChange}>
                            Admin
                        </Checkbox>
                    </Form.Item>
                </Form>
            </div>
            {createdApiKey && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Alert
                        type="success"
                        message={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <Text code style={{ overflowWrap: 'anywhere' }}>
                                    {createdApiKey}
                                </Text>
                                <Tooltip overlay="Copied!" open={copySuccess}>
                                    <Button
                                        onClick={() => copyToClipboard(createdApiKey)}
                                        icon={
                                            copySuccess ?
                                                <CheckOutlined style={{ color: runwayColors.statusGreen }} />
                                            :   <CopyOutlined style={{ color: runwayColors.darkTextGrey }} />
                                        }
                                        type="link"
                                    ></Button>
                                </Tooltip>
                            </div>
                        }
                    ></Alert>
                    <Alert
                        type="warning"
                        message={'Make sure to copy your API key now. You won’t be able to see it again!'}
                    ></Alert>
                </div>
            )}
        </Modal>
    );
};

export default AddApiKeyModal;
